import { Navigate, useSearchParams } from "react-router-dom";
import { PageSkeleton } from "../components/PageSkeleton";
import { useCarteraProyectos, useRecipe } from "../lib/api/queries";
import { useActiveProjectId } from "../lib/activeProject";
import { useQueryView } from "../lib/loading/useQueryView";
import { hasContextualProjectNav } from "../lib/nav";
import { Observaciones } from "./Observaciones";
import { ObservacionesBodySkeleton } from "./Observaciones.skeleton";

// Resolves what `path="observaciones"` does by capability — never by
// `recipe.slug` (§0.4(6)).
//
// A vertical that frames its nav around the project (panel postventa v2,
// `hasContextualProjectNav`: group-rooted, observation lifecycle, no
// facility dashboard) has no org-wide list any more (decisión 4 del
// 2026-09-29): the legacy URL — and the retired «Por revisar» queue at
// `?revision=pendiente` — redirect to the active project's observations.
//
// Everything else keeps the org-wide list. That includes facility, which
// since ADR-0134 ALSO has an observation lifecycle: dispatching on
// `observationStatuses.length > 0` (the pre-ADR-0134 gate) bounced
// facility into postventa's project flow, keyed on a zona.
//
// While the recipe is still loading the list page mounts with its header
// and body skeleton (ADR-0166): it keeps its /facility-dashboard read
// disabled until the recipe is known — that fetch 403s for a postventa
// org that just hasn't resolved its recipe yet (ADR-0072 Phase B) — and
// owns the recipe's failure too, so the route never renders a blank.
export function ObservacionesRoute() {
  const { data: recipe } = useRecipe();
  if (recipe && hasContextualProjectNav(recipe)) {
    return <LegacyObservacionesRedirect />;
  }
  return <Observaciones />;
}

// Its own component so the router-bound hooks only mount on the
// project-nav branch: the facility branch above renders with no
// dependency on the active project.
function LegacyObservacionesRedirect() {
  const active = useActiveProjectId();
  // The same query useActiveProjectId reads (same key, no second
  // request) — here only to know whether it is really on its way.
  const cartera = useQueryView(useCarteraProyectos());
  const [searchParams] = useSearchParams();
  if (active === undefined) {
    // Cartera still resolving: the list frame, never a premature bounce
    // to /proyectos for a user who does have a project. Anything else
    // with no cartera (failed, or not coming) has no project to resolve.
    if (cartera.kind === "loading") {
      return (
        <div className="page">
          <div className="page-header">
            <div className="page-header__lead">
              <h1 className="page-header__title">Observaciones</h1>
            </div>
          </div>
          <PageSkeleton label="Cargando tus observaciones…" visible={cartera.visible}>
            <ObservacionesBodySkeleton />
          </PageSkeleton>
        </div>
      );
    }
    return <Navigate to="/proyectos" replace />;
  }
  if (active === null) return <Navigate to="/proyectos" replace />;
  // «Por revisar» became the project list's «Pendientes de gestión»
  // group; accepting or assigning there is the human review.
  const pendientes = searchParams.get("revision") === "pendiente";
  return (
    <Navigate
      to={`/proyectos/${active}/observaciones${pendientes ? "?f=pendientes" : ""}`}
      replace
    />
  );
}
